import {
  confirm,
  createExpandableColumn,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import type { InstructorDTO } from "@/modules/master/domain/contracts";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  EmploymentTypeBadge,
  InstructorAvatar,
  InstructorStatusBadge,
  MonthlyFreeQuotaBadge,
  PayoutRatesCellSummary,
  PayoutRatesExpandedTable,
} from "./instructor-display";

type InstructorRow = InstructorDTO;

type InstructorsTableProps = {
  data: InstructorRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewProfile?: (instructor: InstructorRow) => void;
  onEdit: (instructor: InstructorRow) => void;
  onDelete: (id: string) => void;
};

export function InstructorsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewProfile,
  onEdit,
  onDelete,
}: InstructorsTableProps) {
  const columns: TanstackReactTable.ColumnDef<InstructorRow>[] = [
    createExpandableColumn<InstructorRow>(""),
    createSortableColumn("name", "ຄູສອນ", {
      cell: ({ row }) => {
        const { id, name, phoneNumber, email } = row.original;
        return (
          <div className="flex min-w-[180px] items-center gap-3 py-0.5">
            <InstructorAvatar name={name} className="size-8 text-xs" />
            <div className="min-w-0">
              <Link
                to="/app/master/instructors/$id"
                params={{ id }}
                className="block truncate font-medium text-primary text-sm hover:underline"
              >
                {name}
              </Link>
              {(phoneNumber || email) && (
                <p className="truncate text-muted-foreground text-xs">
                  {phoneNumber || email}
                </p>
              )}
            </div>
          </div>
        );
      },
    }),
    {
      header: "ປະເພດ / Quota",
      id: "employmentQuota",
      cell: ({ row }) => (
        <div className="flex min-w-[120px] flex-col gap-1.5 py-0.5">
          <EmploymentTypeBadge type={row.original.employmentType} />
          <MonthlyFreeQuotaBadge
            quota={row.original.monthlyFreeGeneralClassSessions}
            compact
          />
        </div>
      ),
    },
    {
      header: "ອັດຕາຄ່າຈ້າງ",
      accessorKey: "payoutRates",
      cell: ({ row }) => (
        <PayoutRatesCellSummary rates={row.original.payoutRates} />
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => (
        <InstructorStatusBadge status={row.original.status} />
      ),
    },
    {
      header: "ວັນທີສ້າງ",
      accessorKey: "createdAt",
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground text-xs tabular-nums">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: InstructorRow } }) => {
        const { id } = row.original;
        const actions = [];

        if (onViewProfile) {
          actions.push({
            label: "ເບິ່ງໂປຣໄຟລ໌",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onViewProfile(row.original),
          });
        }

        actions.push({
          label: "ແກ້ໄຂ",
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => onEdit(row.original),
        });

        actions.push({
          label: "ລຶບ",
          variant: "destructive" as const,
          icon: <TrashIcon className="h-4 w-4" />,
          onClick: async () => {
            const ok = await confirm({
              title: "ລຶບຄູສອນ",
              description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄູສອນນີ້?",
              actionText: "ລຶບ",
              ActionProps: {
                variant: "destructive",
              },
            });
            if (ok) onDelete(id);
          },
        });

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<InstructorRow, unknown>
      noDataMessage="ບໍ່ພົບຄູສອນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      renderExpandedContent={({ original }) => (
        <div className="space-y-2 rounded-lg bg-muted/30 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-medium text-sm">
              ອັດຕາຄ່າຈ້າງ — {original.name}
            </h4>
            <span className="text-muted-foreground text-xs tabular-nums">
              {
                original.payoutRates.filter((r) => r.amountKip > 0).length
              }{" "}
              / {original.payoutRates.length} slot ມີຄ່າ
            </span>
          </div>
          <PayoutRatesExpandedTable rates={original.payoutRates} />
        </div>
      )}
    />
  );
}
